/**
 * /acceptance-criteria — SEO page for "acceptance criteria" (~188 traffic,
 * vol 2.7k, strong fit). Defines acceptance criteria, gives the Given/When/
 * Then + checklist forms and a template, then the Draftlize turn: criteria
 * are written against a spec that moves, and a copied criterion silently
 * disagrees with the spec it came from.
 *
 * English-only + inline. Chinese variant in AcceptanceCriteriaZh.tsx.
 */
import { MarketingShell } from './MarketingShell';

export function AcceptanceCriteria() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/acceptance-criteria"
      documentTitle="Acceptance Criteria: Examples + Template · Draftlize"
      documentDescription="What acceptance criteria are, the Given/When/Then and checklist formats, examples, and a template — plus how to keep them in sync with the spec they test."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Guide · Acceptance criteria</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 62px)', lineHeight: 1.02, margin: '12px 0 24px' }}>
            Acceptance criteria,<br/>
            <em>and keeping them true to the spec.</em>
          </h1>
          <p className="lede">
            <em>Acceptance criteria</em> are the conditions a feature must satisfy to be considered done — the testable definition of &quot;done&quot; that turns a vague story into something QA, engineering, and the PM can all agree was or wasn&apos;t met. This page covers what they are, the two formats that actually get used (Given/When/Then and the checklist), examples, and a template. Then the honest part: criteria are written against a spec, the spec moves, and a criterion copied into a ticket silently disagrees with the spec it came from.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">So, <em>what are acceptance criteria?</em></h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <p style={{ fontSize: 17, lineHeight: 1.7, color: 'var(--ink-1)', margin: '0 0 18px' }}>
            Acceptance criteria are the specific, testable conditions under which a <a href="/user-story-template" style={{ color: 'var(--amber)' }}>user story</a> or requirement is accepted as complete. They turn &quot;build search&quot; into something checkable: what counts as working, what the edge cases are, what happens when it fails. Their job is to remove ambiguity before code is written, so &quot;is it done?&quot; has an answer everyone agreed on in advance instead of a debate in review.
          </p>
          <p style={{ fontSize: 17, lineHeight: 1.7, color: 'var(--ink-1)', margin: 0 }}>
            They&apos;re distinct from a definition of done (which is a team-wide bar that applies to every story — tests pass, docs updated) and from the requirement itself (which says what to build; criteria say how you&apos;ll know it&apos;s built right). Good criteria are written before development, testable, and focused on outcomes, not implementation.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">Two formats</span>
            <h2 className="serif">How criteria <em>are actually written.</em></h2>
          </div>
          <p>Most acceptance criteria take one of two shapes. Pick by whether behavior is conditional (scenario) or a flat list of must-haves (checklist).</p>
        </div>

        <div style={{ border: '1px solid var(--ink-0)', background: 'var(--paper-0)', fontFamily: "'Inter', sans-serif" }}>
          {[
            { k: 'Given / When / Then', hint: 'Scenario form', body: 'Given [context], When [action], Then [outcome]. E.g. "Given a thread with 3 cards, When I export to PDF, Then all 3 cards appear in order." Best when behavior depends on state — it forces you to name the context, which is where bugs hide. This is the BDD format.' },
            { k: 'Checklist / rule form', hint: 'Flat list', body: 'A bulleted list of conditions that must all be true: "Search returns results in under 500ms", "Empty query shows a hint", "Results are paginated at 20." Best for a set of independent must-haves where there\'s no conditional flow to model.' },
            { k: 'Edge cases & errors', hint: 'Don\'t skip', body: 'The conditions everyone forgets: empty states, failures, permissions, limits. A criterion set that only covers the happy path is half a spec — the missing half is exactly what generates the bug report. Name what happens when it goes wrong.' },
          ].map((f, i) => (
            <div key={f.k} style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 24, padding: '20px 22px', borderTop: i === 0 ? 0 : '1px dashed var(--rule)', alignItems: 'baseline' }}>
              <div>
                <span className="serif" style={{ fontSize: 19, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.2 }}>{f.k}</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9.5, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--amber)', fontWeight: 700 }}>{f.hint}</span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>
        <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, letterSpacing: '.06em', color: 'var(--ink-3)', textAlign: 'center', marginTop: 18 }}>
          A good set: 3–7 criteria, each independently testable, covering the happy path and the obvious failures. More than that usually means the story is too big.
        </p>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">Criteria test a spec. <em>The spec moves.</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            Acceptance criteria are written against a spec at one moment. The spec then changes — a decision reverses, a requirement is re-cut — and the criteria, copied into a ticket, keep testing the old version. Draftlize links the criteria to the spec they came from.
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Criteria are cards linked to the spec</h3>
            <p>In Draftlize a criterion isn&apos;t text pasted into a ticket — it&apos;s tied to the spec card it verifies. The &quot;done when&quot; references the requirement instead of restating it, so the two can&apos;t silently diverge into a ticket that says one thing and a spec that says another.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">The spec changes, criteria flag stale</h3>
            <p>Re-cut the requirement and the criteria that tested the old one auto-flag stale — so QA isn&apos;t the place you discover the acceptance test no longer matches the spec. The mismatch surfaces when the spec moves, not three days into the sprint.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Your agent tests against the current spec</h3>
            <p>When Claude Code or Cursor implements or writes tests over MCP, it reads the criteria and the linked spec together — so what it builds and checks against is the current definition of done, not a Given/When/Then that fossilized two decisions ago.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Acceptance criteria are only as true as the spec they were copied from. The trouble is the spec doesn&apos;t tell the copy when it changed.
        </blockquote>
        <cite>Keep Given/When/Then. Link the criteria to the spec instead of copying it.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Common questions.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: 'What is the difference between acceptance criteria and definition of done?', a: 'Acceptance criteria are specific to one story — the conditions that make that particular feature complete. Definition of done is a team-wide checklist that applies to every story (tests written, code reviewed, docs updated). Criteria say "is this feature right"; definition of done says "is any story shippable."' },
            { q: 'What is the Given/When/Then format?', a: 'A way of writing a criterion as a scenario: Given some context, When an action happens, Then an outcome is expected. It comes from behavior-driven development and is strongest when behavior depends on state, because it forces you to name the context the behavior occurs in.' },
            { q: 'Who writes acceptance criteria?', a: 'Usually the product manager or product owner drafts them, refined with engineering and QA before development starts. Writing them with the people who will build and test the feature is what catches the missing edge cases early, while they are still cheap.' },
            { q: 'How many acceptance criteria should a story have?', a: 'Typically three to seven, each independently testable, covering the happy path and the obvious failure and edge cases. If you need many more, the story is probably too big and should be split.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Keep &quot;done&quot; tied to the spec <em>— free with $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Write criteria in the format above, or as cards in Draftlize linked to the spec they verify — so when the spec moves, the acceptance test flags stale instead of passing against a definition of done that no longer holds.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
